import React from 'react'
import {Stack, Typography, SxProps, Theme, useTheme} from '@mui/material'
import {TimePicker} from '@mui/x-date-pickers/TimePicker'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/it'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {PICKER_LOCALE_TEXT, pickerFieldSx, pickerPopupSlotProps} from './pickerStyles'

interface TimePickerNewProps {
  value: string | null
  onChange: (value: string | null) => void
  label?: string
  required?: boolean
  disabled?: boolean
  error?: boolean
  helperText?: string
  fullWidth?: boolean
  sx?: SxProps<Theme>
  /** Adds an "Adesso" button to the picker popup, which sets the value to the current date and time. */
  showNowAction?: boolean
}

export default function TimePickerNew({
  value,
  onChange,
  label,
  required,
  disabled,
  error,
  helperText,
  fullWidth,
  sx,
  showNowAction,
}: TimePickerNewProps): JSX.Element {
  const theme = useTheme()
  // Ensures the time-selection popup/dialog always renders above any container this
  // component may be nested in (e.g. a Drawer/Dialog with a boosted z-index).
  const popupZIndex = theme.zIndex.modal + 2

  return (
    <Stack spacing={0.5} sx={{width: fullWidth ? '100%' : undefined, ...sx}}>
      {label && (
        <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
          {label}{required && ' *'}
        </Typography>
      )}
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="it"
        localeText={showNowAction ? {...PICKER_LOCALE_TEXT, todayButtonLabel: 'Adesso'} : PICKER_LOCALE_TEXT}
      >
        <TimePicker
          value={value ? dayjs(value) : null}
          onChange={(val) => onChange(val ? val.toISOString() : null)}
          disabled={disabled}
          format="HH:mm"
          ampm={false}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: fullWidth ?? true,
              error,
              sx: pickerFieldSx(error),
            },
            ...pickerPopupSlotProps(popupZIndex),
            ...showNowAction ? {actionBar: {actions: ['today', 'accept']}} : {},
          }}
        />
      </LocalizationProvider>
      {helperText && (
        <Typography variant="caption" sx={{color: error ? 'error.main' : NEUTRAL_SCALE[500]}}>
          {helperText}
        </Typography>
      )}
    </Stack>
  )
}
